import axios from 'axios'
import Auth from '@/utilities/auth'

/*
 * Ekran gezinme izleme servisi.
 * - Vue Router afterEach üzerinden her ekran geçişini yakalar (router/index.js).
 * - Her ekranda geçirilen TOPLAM ve AKTİF süreyi hesaplar (idle-timer + sekme görünürlüğü).
 * - Olayları kuyruğa alır; periyodik olarak ve sekme kapanışında backend'e gönderir.
 * - Sekme kapanışında fetch(keepalive) kullanılır; böylece Authorization header'ı (kimlik) korunur.
 * Kimlik bilgileri backend'de token'dan doğrulanır; burada gönderilmez.
 */

const SESSION_KEY = 'derko_tracking_session'
const FLUSH_INTERVAL_MS = 15000 // periyodik gönderim
const IDLE_THRESHOLD_MS = 60000 // 60 sn hareketsizlik = boşta
const MAX_QUEUE = 200 // kuyruk üst sınırı (gönderim başarısızsa şişmesin)
const TRACK_PATH = '/api/navigation/track'

// ExitType enum (backend Domain.LogDb.Enums.ScreenExitType ile aynı)
const ExitType = {
  Unknown: 0, Navigate: 1, Refresh: 2, Close: 3,
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-axxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16))
}

function toLocalIso(d) {
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

function combineUrl(base, path) {
  if (!base) return path
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

const Tracking = {
  started: false,
  sessionId: null,
  current: null, // { routeName, routePath, pageTitle, fromRouteName, fromRoutePath, enteredAt }
  queue: [],
  flushTimer: null,

  // aktif süre takibi
  activeMs: 0,
  activeSince: null, // aktif periyodun başlangıcı (ms epoch) ya da null (duraklatılmış)
  idleTimer: null,
  isHidden: false,

  isAuthed() {
    return Auth.isAuthenticated || Auth.customerAuthenticated
  },

  start() {
    if (this.started || typeof window === 'undefined') return
    this.started = true

    this.sessionId = sessionStorage.getItem(SESSION_KEY)
    if (!this.sessionId) {
      this.sessionId = uuid()
      sessionStorage.setItem(SESSION_KEY, this.sessionId)
    }

    const activity = () => this.handleUserActivity()
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(evt => window.addEventListener(evt, activity, { passive: true }))

    document.addEventListener('visibilitychange', () => this.handleVisibilityChange())
    window.addEventListener('pagehide', () => this.handlePageHide())

    this.flushTimer = setInterval(() => this.flush(false), FLUSH_INTERVAL_MS)
  },

  // Router afterEach'ten çağrılır
  onNavigate(to, from) {
    if (!this.started) this.start()

    // Önceki ekranı kapat ve kuyruğa al
    this.closeCurrent(ExitType.Navigate)

    if (!this.isAuthed()) {
      this.current = null
      return
    }

    this.current = {
      routeName: to && to.name ? String(to.name) : '',
      routePath: to ? to.path : '',
      pageTitle: to && to.meta && to.meta.pageTitle ? to.meta.pageTitle : '',
      fromRouteName: from && from.name ? String(from.name) : '',
      fromRoutePath: from ? from.path : '',
      enteredAt: new Date(),
    }
    // aktif süre sayacını sıfırla/başlat
    this.activeMs = 0
    this.activeSince = this.isHidden ? null : Date.now()
    this.resetIdleTimer()
  },

  handleUserActivity() {
    if (!this.current) return
    // boştaydıysa yeniden aktif et
    if (this.activeSince === null && !this.isHidden) {
      this.activeSince = Date.now()
    }
    this.resetIdleTimer()
  },

  resetIdleTimer() {
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => this.pauseActive(), IDLE_THRESHOLD_MS)
  },

  pauseActive() {
    if (this.activeSince !== null) {
      this.activeMs += Date.now() - this.activeSince
      this.activeSince = null
    }
  },

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.isHidden = true
      this.pauseActive()
      // biriken (kapanmış) olayları gönder; mevcut ekran açık kalır
      this.flush(true)
    } else {
      this.isHidden = false
      if (this.current && this.activeSince === null) {
        this.activeSince = Date.now()
      }
      this.resetIdleTimer()
    }
  },

  handlePageHide() {
    this.closeCurrent(ExitType.Close)
    this.flush(true)
  },

  closeCurrent(exitType) {
    if (!this.current) return
    this.pauseActive()

    const now = new Date()
    const durationSeconds = Math.max(0, Math.round((now.getTime() - this.current.enteredAt.getTime()) / 1000))
    const activeSeconds = Math.min(durationSeconds, Math.max(0, Math.round(this.activeMs / 1000)))

    this.queue.push({
      SessionId: this.sessionId,
      RouteName: this.current.routeName,
      RoutePath: this.current.routePath,
      PageTitle: this.current.pageTitle,
      FromRouteName: this.current.fromRouteName,
      FromRoutePath: this.current.fromRoutePath,
      EnteredAt: toLocalIso(this.current.enteredAt),
      LeftAt: toLocalIso(now),
      DurationSeconds: durationSeconds,
      ActiveSeconds: activeSeconds,
      ExitType: exitType,
    })

    if (this.queue.length > MAX_QUEUE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE)
    }

    this.current = null
  },

  flush(useKeepalive) {
    if (this.queue.length === 0) return
    const events = this.queue.splice(0, this.queue.length)
    const payload = { Events: events }

    if (useKeepalive && typeof fetch === 'function') {
      try {
        const url = combineUrl(axios.defaults.baseURL, TRACK_PATH)
        const headers = { 'Content-Type': 'application/json' }
        const { token } = Auth
        if (token) headers.Authorization = `Bearer ${token}`
        fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: 'omit',
        }).catch(() => {})
      } catch (e) { /* sessizce yut */ }
      return
    }

    axios.post(TRACK_PATH, payload).catch(() => {
      // başarısızsa olayları geri koy (kapasiteyi aşmayacak şekilde)
      this.queue = events.concat(this.queue).slice(-MAX_QUEUE)
    })
  },
}

export default Tracking
